import { Box, Divider, Stack, Typography } from "@mui/material";
import { Colors } from "Components/Layout/Themes/Colors";
import type { ReactNode } from "react";

type Props = {
  label: string;
  /** Rendered as "-" when empty. */
  value: string | null;
  /** Rendered inline right after the value (e.g. the verification badge). */
  valueAdornment?: ReactNode;
  /** Top-right slot (e.g. an EditLinkButton). */
  action?: ReactNode;
  /**
   * Optional footer row below a divider — e.g. the profile-visibility switch row on the
   * identity card, or a static note. Omitted = the card is just the value block.
   */
  footer?: ReactNode;
};

/**
 * One field of the public-information tab (ZS-1320): a grey rounded card with the value
 * on top (label above it, optional action in the corner) and an optional footer row.
 * Plain Box on purpose — the global MuiPaper boxShadow override would make a Paper float.
 */
export const PublicInfoCard = ({
  label,
  value,
  valueAdornment,
  action,
  footer,
}: Props) => (
  <Box
    p={3}
    borderRadius={2}
    sx={{ backgroundColor: Colors.Gray }}
    display="grid"
    rowGap={2}
  >
    <Box display="flex" justifyContent="space-between" gap={2}>
      <Stack>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography fontWeight={500}>{value || "-"}</Typography>
          {valueAdornment}
        </Stack>
      </Stack>
      {action && (
        <Box display="flex" alignItems="flex-start">
          {action}
        </Box>
      )}
    </Box>
    {footer && (
      <>
        <Divider />
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          // A footer child that sets width 100% drops to its own line (the visibility
          // switch's error row) instead of squeezing the label/control pair on a phone.
          flexWrap="wrap"
        >
          {footer}
        </Box>
      </>
    )}
  </Box>
);
